import React, { useState, useEffect } from "react";
import { Edit2, Trash2, Plus } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Loader from "../../components/common/Loader";

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const fetchCourses = async () => {
    try {
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/courses/get-all`,
        headers: {},
      };
      const startTime = Date.now();

      const response = await axios.request(config);

      if (response.data.success) {
        setCourses(response.data.data);
      }
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(1000 - elapsed, 0);

      setTimeout(() => setIsLoading(false), remaining);
    } catch (err) {
      console.error("Error fetching courses:", err);
      setError(err.message);
      setIsLoading(false);
      setTimeout(() => setIsLoading(false), 1000);
    }
  };
  useEffect(() => {
    fetchCourses();
  }, []);

  const handleCardClick = (courseId) => {
    navigate("/course-details", { state: { courseId: courseId } });
  };

  const handleEdit = (e, courseId) => {
    e.stopPropagation();
    navigate(`/edit-course/${courseId}`);
    console.log("Edit course:", courseId);
  };

  const handleDelete = (e, courseId) => {
    e.stopPropagation();

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        let config = {
          method: "delete",
          maxBodyLength: Infinity,
          url: `${process.env.REACT_APP_API_URL}/courses/delete/${courseId}`,
          headers: {},
        };

        axios
          .request(config)
          .then((response) => {
            console.log("Deleted:", response.data);
            Swal.fire({
              title: "Deleted!",
              text: "The course has been deleted successfully.",
              icon: "success",
              timer: 1500,
              showConfirmButton: false,
            });
            fetchCourses();
          })
          .catch((error) => {
            console.error("Delete error:", error);
            Swal.fire({
              title: "Error!",
              text: "Something went wrong while deleting the course.",
              icon: "error",
            });
          });
      } else {
        Swal.fire({
          title: "Cancelled",
          text: "Your course is safe 😊",
          icon: "info",
          timer: 1200,
          showConfirmButton: false,
        });
      }
    });
  };

  return (
    <div className="min-h-screen p-6 dashboardContainer">
      <div className="w-full mx-auto">
        <div className="mb-4 mt-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-primaryTextColor mb-2">
                Course Management
              </h1>
            </div>
            <button
              className="bg-primaryBtnColor hover:bg-opacity-90 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-300 shadow-md hover:shadow-lg"
              onClick={() => {
                navigate("/add-course");
              }}
            >
              <Plus size={20} />
              Add Course
            </button>
          </div>
          {/* <div className="h-1 w-24 bg-primaryColor rounded"></div> */}
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            <>
              <Loader />
            </>
          ) : error ? (
            <div className="col-span-full text-center py-16">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-2xl font-bold text-primaryTextColor mb-2">
                Error Loading Courses
              </h3>
              <p className="text-secondaryTextColor">{error}</p>
            </div>
          ) : (
            courses.map((course) => (
              <div
                key={course._id}
                onClick={() => handleCardClick(course._id)}
                className="bg-gray-100 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group relative overflow-hidden"
              >
                <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                  <button
                    onClick={(e) => handleEdit(e, course._id)}
                    className="bg-secondaryColor text-white p-2 rounded-full hover:bg-opacity-90 transition-all shadow-md"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={(e) => handleDelete(e, course._id)}
                    className="bg-primaryColor text-white p-2 rounded-full hover:bg-opacity-90 transition-all shadow-md"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="p-6">
                  {/* Category Badge */}
                  <span className="inline-block bg-secondaryColor text-white text-xs font-semibold px-3 py-1 rounded-full mb-3 uppercase">
                    {course.category}
                  </span>

                  {/* Course Name */}
                  <h3 className="text-xl font-bold text-primaryTextColor mb-2 group-hover:text-primaryColor transition-colors">
                    {course.name}
                  </h3>

                  <p className="text-secondaryColor font-semibold text-sm mb-3">
                    {course.instituteName}
                  </p>

                  <p className="text-secondaryTextColor text-sm mb-4 line-clamp-3">
                    {course.description}
                  </p>

                  <div>
                    {course.programs && course.programs.length > 0 ? (
                      course.programs.map((program, index) => (
                        <div
                          key={index}
                          className="flex flex-wrap gap-2 mb-2 last:mb-0"
                        >
                          <span className="bg-gray-100 text-primaryTextColor text-xs px-3 py-1 rounded-full">
                            {program.programType || "Program Type N/A"}
                          </span>
                          <span className="bg-gray-100 text-primaryTextColor text-xs px-3 py-1 rounded-full">
                            {program.duration || "Duration N/A"}
                          </span>
                          <span className="bg-gray-100 text-primaryTextColor text-xs px-3 py-1 rounded-full">
                            {program.credits
                              ? `${program.credits} Credits`
                              : "No Credits"}
                          </span>
                        </div>
                      ))
                    ) : (
                      // Fallback
                      <div className="flex flex-wrap gap-2">
                        <span className="bg-gray-100 text-primaryTextColor text-xs px-3 py-1 rounded-full">
                          {course.programType || "Program Type N/A"}
                        </span>
                        <span className="bg-gray-100 text-primaryTextColor text-xs px-3 py-1 rounded-full">
                          {course.duration || "Duration N/A"}
                        </span>
                        <span className="bg-gray-100 text-primaryTextColor text-xs px-3 py-1 rounded-full">
                          {course.credits
                            ? `${course.credits} Credits`
                            : "No Credits"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Empty State */}
        {!isLoading && courses.length === 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-2xl font-bold text-primaryTextColor mb-2">
              No Courses Found
            </h3>
            <p className="text-secondaryTextColor mb-6">
              Start by adding your first course
            </p>
            <button className="bg-primaryBtnColor hover:bg-opacity-90 text-white px-6 py-3 rounded-lg flex items-center gap-2 mx-auto transition-all duration-300">
              <Plus size={20} />
              Add Course
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Courses;
